import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiUpload, FiX, FiFileText, FiImage } from 'react-icons/fi';

const FileUploadModal = ({
    isOpen,
    onClose,
    onFileSelect,
    fileType = 'image', // 'image' or 'pdf'
    multiple = false,
    maxFiles = 20,
    maxFileSize = 10 * 1024 * 1024, // 10MB
    loading = false
}) => {
    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);

    const validImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const validPdfTypes = ['application/pdf'];

    const validateFile = (file) => {
        if (file.size > maxFileSize) {
            toast.error(`File size should be less than ${Math.round(maxFileSize / (1024 * 1024))}MB`);
            return false;
        }

        if (fileType === 'image' && !validImageTypes.includes(file.type)) {
            toast.error('Please select a valid image file (JPEG, PNG, or WebP)');
            return false;
        }

        if (fileType === 'pdf' && !validPdfTypes.includes(file.type)) {
            toast.error('Please select a valid PDF file');
            return false;
        }

        return true;
    };

    const handleFiles = (files) => {
        const fileArray = Array.from(files);

        if (!multiple && fileArray.length > 1) {
            toast.error('Please select only one file');
            return;
        }

        if (multiple && fileArray.length > maxFiles) {
            toast.error(`You can select up to ${maxFiles} files at a time`);
            return;
        }

        const validFiles = fileArray.filter(validateFile);

        if (validFiles.length > 0) {
            setSelectedFiles(validFiles);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFiles(e.target.files);
        }
    };

    const handleUpload = () => {
        if (selectedFiles.length === 0) {
            toast.error('Please select at least one file');
            return;
        }

        if (multiple) {
            onFileSelect(selectedFiles);
        } else {
            onFileSelect(selectedFiles[0]);
        }

        setSelectedFiles([]);
        onClose();
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const resetModal = () => {
        setSelectedFiles([]);
        setDragActive(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-xl font-semibold text-gray-800">
                        Upload {fileType === 'image' ? 'Images' : 'PDFs'}
                    </h2>
                    <button
                        onClick={resetModal}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <div className="p-6">
                    {/* Drag and Drop Area */}
                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-300 hover:border-gray-400'
                            }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        <div className="flex flex-col items-center space-y-4">
                            {fileType === 'image' ? (
                                <FiImage size={48} className="text-gray-400" />
                            ) : (
                                <FiFileText size={48} className="text-gray-400" />
                            )}

                            <div>
                                <p className="text-lg font-medium text-gray-700">
                                    Drag and drop your {fileType === 'image' ? 'images' : 'PDFs'} here
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    or
                                </p>
                                <button
                                    onClick={openFileDialog}
                                    className="text-primary hover:text-primary/80 font-medium underline"
                                >
                                    browse from computer
                                </button>
                            </div>

                            <p className="text-xs text-gray-400">
                                {fileType === 'image'
                                    ? 'Supports JPEG, PNG, WebP (max 10MB each)'
                                    : 'Supports PDF (max 10MB)'
                                }
                                {multiple && ` • Up to ${maxFiles} files`}
                            </p>
                        </div>
                    </div>

                    {/* Hidden file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={fileType === 'image' ? 'image/*' : 'pdf/*'}
                        multiple={multiple}
                        onChange={handleFileInput}
                        className="hidden"
                    />

                    {/* Selected Files */}
                    {selectedFiles.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">
                                Selected Files ({selectedFiles.length})
                            </h3>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                                {selectedFiles.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                        <div className="flex items-center space-x-3">
                                            {fileType === 'image' ? (
                                                <FiImage size={20} className="text-gray-500" />
                                            ) : (
                                                <FiFileText size={20} className="text-gray-500" />
                                            )}
                                            <div>
                                                <p className="text-sm font-medium text-gray-700 truncate max-w-48">
                                                    {file.name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => removeFile(index)}
                                            className="text-red-500 hover:text-red-700 transition-colors"
                                        >
                                            <FiX size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            onClick={resetModal}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={selectedFiles.length === 0 || loading}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Uploading...</span>
                                </>
                            ) : (
                                <>
                                    <FiUpload size={16} />
                                    <span>Upload {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FileUploadModal;
